/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useRef } from 'react';
import type { PreloadedQuery, EntryPointProps } from 'react-relay';
import { nav4SidebarGlobalProductsSettingsResource } from '@jira/router-resources__navigation-sidebar-global-products-settings/src/nav4SidebarGlobalProductsSettingsResource.tsx';
import { SidebarBoundary } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import { SidebarContent } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/ui/sidebar-content/index.tsx';
import { Skeleton } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/ui/skeleton/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { SidebarReady } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/metrics/index.tsx';
import type { GlobalProductsSettingsSidebarQuery } from './__generated__/GlobalProductsSettingsSidebarQuery.graphql';
import { GlobalProductsSettingsSidebar } from './GlobalProductsSettingsSidebar.tsx';

export function SidebarContentWithRelayResource(): React.JSX.Element {
	const { queryReference } = useRelayResource<GlobalProductsSettingsSidebarQuery>(
		nav4SidebarGlobalProductsSettingsResource,
	);
	const queryReferenceRef = useRef<PreloadedQuery<GlobalProductsSettingsSidebarQuery> | null>(null);

	// We don't want to use null query references. So only remember the non-null ones,
	// and pass the non-null query reference to the children.
	// From Slack: https://atlassian.slack.com/archives/C01E2MPPLE4/p1654736794416059?thread_ts=1654051102.548519&cid=C01E2MPPLE4
	if (queryReference) {
		queryReferenceRef.current = queryReference;
	}

	return <SidebarContentInner queryReference={queryReferenceRef.current} />;
}

type Queries = {
	nav4SidebarGlobalProductsSettings: GlobalProductsSettingsSidebarQuery;
};

export function SidebarContentForEntryPoint({
	queries: { nav4SidebarGlobalProductsSettings },
}: EntryPointProps<Queries, {}, {}, {}>): React.JSX.Element {
	return <SidebarContentInner queryReference={nav4SidebarGlobalProductsSettings} />;
}

type Props = {
	queryReference: PreloadedQuery<GlobalProductsSettingsSidebarQuery> | null;
};

export function SidebarContentInner({ queryReference }: Props): React.JSX.Element {
	return (
		<SidebarBoundary
			id="nav4.sidebar.global-settings.products-settings"
			packageName="sidebarNav4GlobalProductsSettings"
			ufoName="nav4.sidebar.global-settings.products-settings"
		>
			<SidebarContent
				currentPageName="products"
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-apps-sidebar-nav4-sidebars-settings-global-products.ui.sidebar-content"
			>
				<Placeholder name="nav4.sidebar.global-settings.products-settings" fallback={<Skeleton />}>
					{queryReference && <GlobalProductsSettingsSidebar queryReference={queryReference} />}
				</Placeholder>
			</SidebarContent>
			<SidebarReady />
		</SidebarBoundary>
	);
}

export default SidebarContentForEntryPoint;
